import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  getUserDashboardInfo,
  updateAdvisorDashboardUserProfile,
  getAdminUserData,
  getCurrentUserId,
} from '../../store/actions';
import { getAuthState } from '../../store/selectors';
import poweredBy from '../../ASUPhotos/logos/poweredByBlackAndWhite.png';
import { userProfileCopy } from '../../copy/advisor-dashboard/user-profile.json';
import UserProfileForm from './UserProfileForm';
import BlankUserPhoto from '../user-dashboard/utility/BlankUserPhoto';
import Chevron from '../user-dashboard/utility/Chevron';
import GradCapIcon from '../utility/GradCapIcon';
import HomeIcon from '../utility/HomeIcon';
import PersonIcon from '../utility/PersonIcon';
import RibbonIcon from '../utility/RibbonIcon';
import FavoriteIcon from '../utility/FavoriteIcon';
import Loading from '../utility/Loading';
import EditRole from './EditRole';
import DeleteUser from './DeleteUser';
import Top3Careers from '../user-dashboard/Top3Careers';
import RiasecScore from '../user-dashboard/RiasecScore';
import Top25Careers from '../user-dashboard/Top25Careers';
import Favorites from '../user-dashboard/Favorites';
import ClassProgress from '../user-dashboard/ClassProgress';
import ChooseCareerPath from '../ecap/ChooseCareerPath';
import AdditionalActivities from '../ecap/AdditionalActivities';
import AcademicStatistics from '../ecap/AcademicStatistics';
import { getHeaderListItems } from '../utility/headerUtil';
import HamburgerMenu from '../utility/HamburgerMenu';
import SchoolLogo from '../utility/SchoolLogo';
import BrightOutlookModal from '../utility/modal/BrightOutlookModal.jsx';

function UserProfile(props) {
  const [selectedComponent, setSelectedComponent] = useState('User Profile');
  const [brightOutlookModal, setBrightOutlookModal] = useState(false);
  const [isMobile, setIsMobile] = useState(null);
  const [adminUserProfile, setAdminUserProfile] = useState(null);
  const userProfile = React.createRef();
  const classProgress = useRef(null);
  const riasecScore = useRef(null);
  const top25Careers = useRef(null);
  const favorites = useRef(null);

  const Icons = {
    [userProfileCopy.heading1]: HomeIcon,
    [userProfileCopy.heading2]: GradCapIcon,
    [userProfileCopy.heading3]: PersonIcon,
    [userProfileCopy.heading4]: RibbonIcon,
    [userProfileCopy.heading5]: FavoriteIcon,
  };

  const getStartingPoint = (ref) => {
    let startingPoint = 'userProfile';
    if (ref.current) {
      startingPoint = isMobile
        ? ref.current.offsetTop + 95
        : ref.current.offsetTop;
    }
    return startingPoint;
  };

  const getEndingPoint = (ref, startingPoint) => {
    if (ref.current) {
      return startingPoint + ref.current.scrollHeight;
    }
  };

  const numFallsWithinRange = (numToCheck, ref) => {
    const startingPoint = getStartingPoint(ref);
    const endPoint = getEndingPoint(ref, startingPoint);
    return numToCheck >= startingPoint && numToCheck <= endPoint;
  };

  const updateSelectedComponent = (heading) => {
    setSelectedComponent(userProfileCopy[heading]);
  };

  const handleScroll = async (vals) => {
    const currentPlace = vals.scrollTop + window.innerHeight / 2;
    let currentlyActiveElement = 'heading1';
    if (
      classProgress.current &&
      numFallsWithinRange(currentPlace, classProgress)
    ) {
      currentlyActiveElement = 'heading2';
    }
    if (numFallsWithinRange(currentPlace, riasecScore)) {
      currentlyActiveElement = 'heading3';
    }
    if (numFallsWithinRange(currentPlace, top25Careers)) {
      currentlyActiveElement = 'heading4';
    }
    if (currentPlace >= getStartingPoint(favorites)) {
      currentlyActiveElement = 'heading5';
    }
    if (selectedComponent !== currentlyActiveElement) {
      updateSelectedComponent(currentlyActiveElement);
    }
  };

  const getIds = props.location.search.split('&');
  const userIdByURL = Number(getIds[0].split('=')[1]);
  const tenantIdByURL = Number(getIds[1].split('=')[1]);

  const fetchData = useCallback(async () => {
    await props.getUserDashboardInfo(userIdByURL, tenantIdByURL);
  }, []);

  useEffect(() => {
    fetchData().catch(console.error);

    if (window.innerWidth < 800) {
      setIsMobile(window.innerWidth < 800);
    }
    if (window.innerWidth < 1180) {
      setIsMobile(window.innerWidth < 1180);
    }

    const scrollbar = document.querySelector('.ScrollbarsCustom-Scroller');
    scrollbar.addEventListener('scroll', () => handleScroll(scrollbar));
  }, [window.innerWidth, fetchData]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = props.auth.jwtToken;
        if (!token) {
          console.error('Token not found');
          return;
        }

        // Call to get current user ID using the token
        const userId = await props.getCurrentUserId(token);

        if (userId) {
          // If userId is present, fetch user data
          const data = await props.getAdminUserData(userId, token);
          // Store the response in state
          setAdminUserProfile(data);
        } else {
          console.error('User ID not found');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [props.auth.jwtToken, props.getCurrentUserId, props.getAdminUserData]);

  const borderSecondary = {
    borderColor: `${props.userProfile?.tenant?.secondary}`,
  };

  const borderTransparent = {
    borderColor: `transparent`,
  };

  const backgroundSecondary = {
    backgroundColor: `${props.userProfile?.tenant?.secondary}`,
  };

  const borderTopSecondary = {
    borderTop: `9px solid ${props.tenant.secondary}`,
  };

  const getListItems = () => {
    return getHeaderListItems({
      educationLevel: props.userProfile?.educationLevel,
      tenantName: props.userProfile?.tenant,
    });
  };

  const navigateToUsers = () => {
    props.history.push('users');
  };

  const returnIconComponent = (heading) => {
    const copy = userProfileCopy[heading];
    const Icon = Icons[copy];
    if (!Icon) return <div className="no_block_type" />;
    return (
      <div
        className={[
          'menu-svg',
          copy === userProfileCopy.heading1 ? 'home-icon' : '',
        ].join(' ')}
      >
        <Icon color={selectedComponent === copy ? 'black' : '#A7A7A7'} />
      </div>
    );
  };

  const handleScrollToSection = (ref) => {
    const offset = getStartingPoint(ref);
    ref.current.scrollIntoView({
      behavior: 'smooth',
      top: offset,
    });
  };

  const displayMenuItem = (heading, marginBottom, ref) => {
    return (
      <button
        type="button"
        onClick={() => {
          handleScrollToSection(ref);
        }}
        className={
          marginBottom ? 'menu-item menu-item-margin-bottom' : 'menu-item'
        }
        style={
          userProfileCopy[heading] === selectedComponent
            ? borderSecondary
            : borderTransparent
        }
      >
        {returnIconComponent(heading)}
        <p
          className={
            userProfileCopy[heading] === selectedComponent
              ? 'user-profile-menu-heading'
              : 'user-profile-menu-heading unselected'
          }
        >
          {`${userProfileCopy[heading]}`}
        </p>
      </button>
    );
  };

  const displayLeftNav = () => {
    return (
      <>
        <div className="user-profile-left-nav">
          <div className="profile-photo">
            <div className="profile-pic-container">
              {props.userProfile?.profilePhotoUrl ? (
                <img
                  className="user-dashboard-profile-photo"
                  src={props.userProfile?.profilePhotoUrl}
                  alt="user"
                  role="presentation"
                />
              ) : (
                <div className="white-circle">
                  <BlankUserPhoto color={props.userProfile?.tenant.primary} />
                </div>
              )}
            </div>
          </div>
          {props.userProfile && (
            <>
              <div className="user-name">{`${props.userProfile?.firstName} ${props.userProfile?.lastName}`}</div>
            </>
          )}
          <div className="section-divider-nav" />
          <div className="navigation-section">
            {displayMenuItem('heading1', true, userProfile)}
            {props.userProfile?.educationLevel === 'Currently in high school' &&
              props.userProfile?.tenant.features.includes('highschool') &&
              displayMenuItem('heading2', true, classProgress)}
            {displayMenuItem('heading3', true, riasecScore)}
            {displayMenuItem('heading4', true, top25Careers)}
            {displayMenuItem('heading5', false, favorites)}
          </div>
          <div className="section-divider" />
          <div className="bottom-section" />
        </div>
      </>
    );
  };

  const setBrightOutlookModalMethod = () => {
    setBrightOutlookModal(!brightOutlookModal);
  };

  return (
    <>
      {brightOutlookModal && (
        <BrightOutlookModal
          tenant={props.tenant}
          onClickParent={setBrightOutlookModalMethod}
        />
      )}
      <div
        className="advisor-dashboard-base-dashboard-header"
        style={borderTopSecondary}
        ref={userProfile}
      >
        <div className="left-side-header">
          <SchoolLogo />
        </div>
        <HamburgerMenu
          tenant={props.tenant}
          gtmLocation="dashboard hamburger menu"
          listItems={getListItems()}
        />
      </div>
      <div id="user-profile-container">
        {props.userProfile.id &&
        userIdByURL === props.userProfile.id &&
        tenantIdByURL === props.userProfile.tenant.id ? (
          <>
            <div className="profile-nav">{displayLeftNav()}</div>
            <div id="user-profile">
              <div className="header-container">
                <button
                  className="link-button return-to-search"
                  type="button"
                  onClick={() => navigateToUsers()}
                  onKeyPress={() => navigateToUsers()}
                  alt="return to search"
                  style={{ color: props.userProfile?.tenant?.primary }}
                >
                  <Chevron
                    transform="rotate(180deg)"
                    color={props.userProfile?.tenant?.primary}
                  />
                  &nbsp;
                  {userProfileCopy.buttons.link1}
                </button>
                <span className="user-profile-header">
                  {userProfileCopy.heading1}
                  <div className="yellow-bar" style={backgroundSecondary} />
                </span>
                <span className="account-creation">
                  {userProfileCopy.heading6}
                  :&nbsp;
                  {moment(props.userProfile?.primaryAccountCreated).format(
                    'MM/DD/YYYY'
                  )}
                </span>
              </div>
              <div id="userProfileFormRef">
                <UserProfileForm userProfile={props.userProfile} />
              </div>
              {props.userProfile?.editable && (
                <>
                  <div className="section-divider" />
                  <EditRole
                    updateAdvisorDashboardUserProfile={
                      props.updateAdvisorDashboardUserProfile
                    }
                  />
                </>
              )}
              {props.userProfile?.editable &&
                props.userProfile?.tenantName === 'ASU' &&
                !props.userProfile?.isSuperUser &&
                adminUserProfile?.user?.isSuperUser && (
                  <>
                    <div className="section-divider" />
                    <DeleteUser
                      updateAdvisorDashboardUserProfile={
                        props.updateAdvisorDashboardUserProfile
                      }
                    />
                  </>
                )}
              <div className="section-divider" />
              <Top3Careers
                tenant={props.userProfile?.tenant}
                setBrightOutlookModal={setBrightOutlookModalMethod}
                favorites={props.userProfile?.favorites}
              />
              <div className="section-divider" />
              {props.userProfile?.educationLevel ===
                'Currently in high school' &&
                props.userProfile?.tenant.features.includes('highschool') && (
                  <>
                    <div ref={classProgress} id="classProgressRef">
                      <ClassProgress tenant={props.userProfile?.tenant} />
                    </div>
                    <div className="section-divider" />
                  </>
                )}

              <div ref={riasecScore} id="riasecScoreRef">
                <RiasecScore
                  isMobile={isMobile}
                  location={props.location}
                  tenant={props.userProfile?.tenant}
                />
              </div>
              <div className="section-divider" />
              <div ref={top25Careers} id="top25CareersRef">
                <Top25Careers
                  tenant={props.userProfile?.tenant}
                  favorites={props.userProfile?.favorites}
                />
              </div>
              <div className="section-divider" />
              <div ref={favorites}>
                <Favorites
                  tenant={props.userProfile?.tenant}
                  favorites={props.userProfile?.favorites}
                  userId={props.userProfile?.id}
                  location={props.location}
                  allTenantCareers={props.userProfile?.allTenantCareers}
                />
              </div>
              {props.userProfile?.educationLevel ===
                'Currently in high school' &&
                props.userProfile?.tenant.features.includes('highschool') && (
                  <>
                    <div className="section-divider" />
                    <ChooseCareerPath
                      tenant={props.userProfile?.tenant}
                      onRef={() => {}}
                      ecapPlan={props.userProfile?.ecapPlan}
                    />
                    <AdditionalActivities
                      tenant={props.userProfile?.tenant}
                      onRef={() => {}}
                      ecapPlan={props.userProfile?.ecapPlan}
                      ecapActivities={props.userProfile?.ecapActivities}
                    />
                    <AcademicStatistics
                      tenant={props.userProfile?.tenant}
                      onRef={() => {}}
                      ecapPlan={props.userProfile?.ecapPlan}
                    />
                  </>
                )}
              <a
                className="powered-by"
                href="https://www.asu.edu/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={poweredBy}
                  alt="Powered By Arizona State University"
                />
              </a>
            </div>
          </>
        ) : (
          <Loading height="50vh" />
        )}
      </div>
    </>
  );
}

const mapDispatchToProps = { getUserDashboardInfo };

function mapStateToProps(state) {
  const userProfile = state.admin.userProfileInfo;
  const auth = getAuthState(state);

  return { userProfile, auth };
}

export default withRouter(
  connect(mapStateToProps, {
    updateAdvisorDashboardUserProfile,
    getUserDashboardInfo,
    getCurrentUserId,
    getAdminUserData,
    mapDispatchToProps,
  })(UserProfile)
);
